import type { Session } from '@/api/client';

/** Сессии типа «монтаж» не входят в счётчики кнопки сессий в таблице проектов (см. ProjectCell). */
export function isMontageSession(session: Pick<Session, 'session_type'>): boolean {
  return (session.session_type ?? 'запись') === 'монтаж';
}

export type ProjectSessionsPopoverPartition = {
  planned: Session[];
  past: Session[];
  montage: Session[];
  canceled: Session[];
};

/**
 * Разбиение сессий проекта для попапа: каждая сессия ровно в одном кортеже.
 * Отменённые (включая монтаж) только во вкладке «Отменённые».
 */
export function partitionProjectSessionsPopover(sessions: Session[]): ProjectSessionsPopoverPartition {
  const planned: Session[] = [];
  const past: Session[] = [];
  const montage: Session[] = [];
  const canceled: Session[] = [];
  for (const s of sessions) {
    if (s.is_canceled) {
      canceled.push(s);
      continue;
    }
    if (isMontageSession(s)) {
      montage.push(s);
      continue;
    }
    if (s.is_recorded) past.push(s);
    else planned.push(s);
  }
  return { planned, past, montage, canceled };
}

export const SESSION_PROJECT_NOT_SET_LABEL = 'Без проекта';

/** Студия сессии для подписи: 1–4 → «N-я студия», 5 → «1-я монтажная», 6 → «2-я монтажная», иначе «—». */
export function formatSessionStudio(studio: number | string | null | undefined): string {
  const n = typeof studio === 'number' ? studio : parseInt(String(studio || ''), 10);
  if (n === 5) return '1-я монтажная';
  if (n === 6) return '2-я монтажная';
  if (n >= 1 && n <= 4) return `${n}-я студия`;
  return '—';
}

export function getSessionReaderIds(
  session: Pick<Session, 'readers' | 'reader_ids' | 'reader_id'>
): string[] {
  if (session.readers?.length) return session.readers.map((r) => r.id);
  if (session.reader_ids?.length) return session.reader_ids;
  return session.reader_id ? [session.reader_id] : [];
}

export function isPlannedPopoverSession(
  session: Pick<Session, 'is_canceled' | 'session_type' | 'is_recorded'>
): boolean {
  if (session.is_canceled) return false;
  if (isMontageSession(session)) return false;
  return !session.is_recorded;
}

export function sessionMatchesReaderIds(
  session: Pick<Session, 'readers' | 'reader_ids' | 'reader_id'>,
  readerIds: readonly string[]
): boolean {
  if (!readerIds.length) return false;
  const set = new Set(readerIds);
  return getSessionReaderIds(session).some((id) => set.has(id));
}

export function belongsToReaderPlannedOtherCache(
  session: Pick<
    Session,
    'readers' | 'reader_ids' | 'reader_id' | 'project_id' | 'is_canceled' | 'session_type' | 'is_recorded'
  >,
  readerIds: readonly string[],
  excludeProjectId: string
): boolean {
  if (!isPlannedPopoverSession(session)) return false;
  if (!sessionMatchesReaderIds(session, readerIds)) return false;
  if (session.project_id === excludeProjectId) return false;
  return true;
}

export function sortSessionsByDateStartAsc(sessions: Session[]): Session[] {
  return [...sessions].sort((a, b) => {
    const dateDiff = (a.date ?? '').localeCompare(b.date ?? '');
    if (dateDiff !== 0) return dateDiff;
    return (a.start ?? '').localeCompare(b.start ?? '');
  });
}

export function filterReaderOtherPlannedSessions(
  sessions: Session[],
  readerIds: readonly string[],
  excludeProjectId: string
): Session[] {
  return sortSessionsByDateStartAsc(
    sessions.filter((s) => belongsToReaderPlannedOtherCache(s, readerIds, excludeProjectId))
  );
}
